import { useState, type FormEvent } from 'react';
import { Navigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, Card, Modal, PageState, TextField, useToast } from '../../components/ui';
import { useAuth } from '../../auth/AuthContext';
import { apiClient, type ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';

interface PlatformAdmin {
  userId: string;
  email: string;
  displayName: string;
  status: string;
  lastLoginAt: string | null;
}

const KEY = ['admin', 'platform-admins'] as const;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AddAdminModal({ onClose }: { onClose: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [error, setError] = useState<string | undefined>();
  const grant = useMutation<PlatformAdmin, ApiClientError, string>({
    mutationFn: (value) =>
      apiClient.post<PlatformAdmin>('/admin/platform-admins', { email: value }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL.test(value)) {
      setError('Enter a valid email address.');
      return;
    }
    try {
      const added = await grant.mutateAsync(value);
      toast.success(
        `${added.displayName} is now a platform admin. They'll see Admin after signing in again.`,
      );
      onClose();
    } catch (err) {
      const fields = fieldErrorsFromApiError(err);
      setError(fields.email ?? (err instanceof Error ? err.message : 'Could not add this person.'));
    }
  }

  return (
    <Modal title="Add platform admin" onClose={onClose}>
      <form
        onSubmit={(e) => void onSubmit(e)}
        noValidate
        style={{ display: 'grid', gap: 'var(--space-3)' }}
      >
        <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
          Platform admins can manage every tenant, plans, billing and the Admin area. The person
          needs an active account: invite them to a workspace first and ask them to accept.
        </p>
        <TextField
          label="Email of their account"
          type="email"
          autoFocus
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(undefined);
          }}
          error={error}
        />
        <div style={{ display: 'flex', gap: 'var(--space-2)', justifyContent: 'flex-end' }}>
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={grant.isPending}>
            {grant.isPending ? 'Adding…' : 'Make platform admin'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

/**
 * Platform Admin → Platform admins: who may use the cross-tenant Admin area.
 * Grant to an existing active account by email, or remove. You can't remove
 * yourself, and the last platform admin can't be removed - to hand over,
 * add the new person, let them sign in, then have them remove you.
 */
export function PlatformAdminsPage() {
  const { isPlatformAdmin, user } = useAuth();
  const toast = useToast();
  const qc = useQueryClient();
  const [adding, setAdding] = useState(false);
  const admins = useQuery<PlatformAdmin[], ApiClientError>({
    queryKey: KEY,
    queryFn: () => apiClient.get<PlatformAdmin[]>('/admin/platform-admins'),
    enabled: isPlatformAdmin,
  });
  const revoke = useMutation<void, ApiClientError, string>({
    mutationFn: (userId) => apiClient.delete<void>(`/admin/platform-admins/${userId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });

  if (!isPlatformAdmin) return <Navigate to="/" replace />;

  async function onRemove(a: PlatformAdmin) {
    if (!window.confirm(`Remove platform admin access for ${a.email}?`)) return;
    try {
      await revoke.mutateAsync(a.userId);
      toast.success(`${a.email} is no longer a platform admin.`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not remove this admin.');
    }
  }

  const list = admins.data ?? [];

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)', maxWidth: 900 }}>
      <div
        style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', flexWrap: 'wrap' }}
      >
        <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>Platform admins</h1>
        <span style={{ flex: 1 }} />
        <Button onClick={() => setAdding(true)}>Add platform admin</Button>
      </div>
      <p style={{ margin: 0, color: 'var(--color-text-muted)' }}>
        People who can use the Admin area across all tenants. To hand over ownership: add the new
        person, have them sign in once, then let them remove your access. Changes apply at their
        next sign-in.
      </p>

      {adding ? <AddAdminModal onClose={() => setAdding(false)} /> : null}

      <Card>
        {admins.isPending ? (
          <PageState kind="loading" message="Loading platform admins…" />
        ) : admins.isError ? (
          <PageState
            kind="error"
            message={admins.error.message}
            onRetry={() => void admins.refetch()}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="ne-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Last sign-in</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {list.map((a) => {
                  const isMe = a.userId === user?.id;
                  const isLast = list.length <= 1;
                  return (
                    <tr key={a.userId} style={{ cursor: 'default' }}>
                      <td>
                        {a.displayName}
                        {isMe ? (
                          <span className="ne-badge" style={{ marginLeft: 8 }}>
                            You
                          </span>
                        ) : null}
                      </td>
                      <td>{a.email}</td>
                      <td>{a.lastLoginAt ? new Date(a.lastLoginAt).toLocaleString() : 'Never'}</td>
                      <td>
                        <Button
                          variant="secondary"
                          disabled={isMe || isLast || revoke.isPending}
                          title={
                            isMe
                              ? 'You cannot remove yourself - ask another platform admin.'
                              : isLast
                                ? 'The last platform admin cannot be removed.'
                                : undefined
                          }
                          aria-label={`Remove ${a.email}`}
                          onClick={() => void onRemove(a)}
                        >
                          Remove
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
